import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { AdminLayout } from '../components/AdminLayout';
import { expensesApi, type Expense } from '../services/expensesApi';
import { getSession } from '../services/googleAuth';
import {
  Trash2, UploadCloud, AlertCircle,
  TrendingDown, TrendingUp, Calendar, PieChart,
  Receipt, Download, Sparkles, Youtube, Tv, Music, Wifi, Gamepad2, CreditCard, X
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PieChart as RePieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';

const PIE_COLORS = ['#8B5CF6', '#EC4899', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#6366F1', '#14B8A6'];

// Dictionnaire de correspondances pour les emojis par catégorie
const CATEGORY_EMOJIS: Record<string, string> = {
  'Logement': '🏠',
  'Loyer': '🏠',
  'Alimentation': '🛒',
  'Courses': '🛒',
  'Transport': '🚗',
  'Essence': '⛽',
  'Assurance': '🛡️',
  'Santé': '⚕️',
  'Loisirs': '🎮',
  'Abonnement': '📺',
  'Streaming': '🍿',
  'Internet': '🌐',
  'Téléphone': '📱',
  'Salaire': '💰',
  'Revenu': '💸',
  'Virement': '🏦',
  'Électricité': '⚡',
  'Eau': '💧',
  'Gaz': '🚰',
  'Impôts': '🏛️',
  'Restaurant': '🍽️',
  'Cadeaux': '🎁',
  'Animaux': '🐾',
};

const getCategoryEmoji = (category: string) => {
  for (const [key, emoji] of Object.entries(CATEGORY_EMOJIS)) {
    if (category.toLowerCase().includes(key.toLowerCase())) {
      return emoji;
    }
  }
  return '🏷️';
};

// Reconnaissance d'icônes pour abonnements connus
const getSubscriptionIcon = (name: string) => {
  const lowerName = name.toLowerCase();
  if (lowerName.includes('netflix')) return <Tv className="w-4 h-4 text-red-600" />;
  if (lowerName.includes('spotify') || lowerName.includes('apple music') || lowerName.includes('deezer')) return <Music className="w-4 h-4 text-green-500" />;
  if (lowerName.includes('youtube')) return <Youtube className="w-4 h-4 text-red-500" />;
  if (lowerName.includes('internet') || lowerName.includes('orange') || lowerName.includes('sfr') || lowerName.includes('free')) return <Wifi className="w-4 h-4 text-blue-500" />;
  if (lowerName.includes('xbox') || lowerName.includes('playstation') || lowerName.includes('nintendo')) return <Gamepad2 className="w-4 h-4 text-green-600" />;
  return <CreditCard className="w-4 h-4 text-purple-500" />;
};

const renderAiAdvice = (text: string) => {
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, index) => {
    if (index % 2 === 1) {
      return <strong key={index}>{part}</strong>;
    }
    return part.split('\n').map((line, lineIndex, arr) => (
      <React.Fragment key={`${index}-${lineIndex}`}>
        {line}
        {lineIndex < arr.length - 1 && <br />}
      </React.Fragment>
    ));
  });
};

export function AdminExpenses() {
  const { user } = useAuth();

  // États principaux
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // États Filtres
  const [filterMonth, setFilterMonth] = useState<string>(''); // Afficher toutes les dépenses par défaut
  const [filterCategory, setFilterCategory] = useState<string | null>(null);
  const [filterMinAmount, setFilterMinAmount] = useState<string>('');
  const [filterMaxAmount, setFilterMaxAmount] = useState<string>('');

  // États IA (Conseil)
  const [aiAdvice, setAiAdvice] = useState<string | null>(null);
  const [isGeneratingAdvice, setIsGeneratingAdvice] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const { token } = getSession();
    if (token && user) {
      loadExpenses(token);
    }
  }, [user]);

  const loadExpenses = async (token: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await expensesApi.getExpenses(token);
      setExpenses(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const { token } = getSession();

    // Annule si pas de fichier ou pas d'utilisateur
    if (!file || !token) return;

    if (file.type !== 'application/pdf') {
      setError('Veuillez fournir un fichier au format PDF.');
      return;
    }

    try {
      setIsUploading(true);
      setError(null);

      const newExpenses = await expensesApi.uploadPdf(token, file);

      setExpenses((prev) =>
        [...newExpenses, ...prev].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      );

      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Erreur lors de l'analyse IA");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    const { token } = getSession();
    if (!token) return;

    if (!window.confirm('Confirmez-vous la suppression de cette opération ?')) return;

    try {
      await expensesApi.deleteExpense(token, id);
      setExpenses(expenses.filter(e => e.id !== id));
      if (filterCategory && expenses.filter(e => e.id !== id && e.category === filterCategory).length === 0) {
        setFilterCategory(null);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Impossible de supprimer l'élément");
    }
  };

  const handleGenerateAdvice = async () => {
    const { token } = getSession();
    if (!token) return;

    if (filteredExpenses.length === 0) {
        setAiAdvice("Aucune donnée à analyser pour cette période/ce filtre.");
        return;
    }

    try {
      setIsGeneratingAdvice(true);
      setAiAdvice(null);
      const advice = await expensesApi.getAdvice(token, filteredExpenses);
      setAiAdvice(advice);
    } catch (err: unknown) {
      setAiAdvice(`Erreur: Impossible de joindre l'IA Gemini. ${err instanceof Error ? err.message : ''}`);
    } finally {
      setIsGeneratingAdvice(false);
    }
  };

  // --- FILTRES ---
  const filteredExpenses = useMemo(() => {
    return expenses.filter(e => {
      // 1. Filtre Mois (format e.date = ISO "YYYY-MM-DD...")
      if (filterMonth && !e.date.startsWith(filterMonth)) return false;

      // 2. Filtre Catégorie
      if (filterCategory && e.category !== filterCategory) return false;

      // 3. Filtres Montant (appliquer sur les dépenses comme sur les revenus)
      const min = parseFloat(filterMinAmount);
      const max = parseFloat(filterMaxAmount);
      if (!isNaN(min) && e.amount < min) return false;
      if (!isNaN(max) && e.amount > max) return false;

      return true;
    });
  }, [expenses, filterMonth, filterCategory, filterMinAmount, filterMaxAmount]);

  // --- CALCULS (Sur éléments filtrés) ---
  const totalExpenses = filteredExpenses.filter(e => e.type === 'expense').reduce((sum, e) => sum + e.amount, 0);
  const totalIncome = filteredExpenses.filter(e => e.type === 'income').reduce((sum, e) => sum + e.amount, 0);
  const balance = totalIncome - totalExpenses;

  const categoryBreakdown = filteredExpenses
    .filter(e => e.type === 'expense') // On ne fait le camembert/répartition que pour les dépenses
    .reduce((acc, expense) => {
      acc[expense.category] = (acc[expense.category] || 0) + expense.amount;
      return acc;
    }, {} as Record<string, number>);

  const sortedCategories = Object.entries(categoryBreakdown).sort(([, a], [, b]) => b - a);

  // Détection des abonnements : Une dépense (même nom) récurrente sur au moins 2 mois différents
  const subscriptions = useMemo(() => {
    // 1. Grouper par nom normalisé => Set des mois (Global sur toutes les dépenses non filtrées)
    const monthsByName: Record<string, Set<string>> = {};
    expenses.forEach(e => {
      if (e.type !== 'expense') return; // uniquement les dépenses
      const key = e.name.toLowerCase().trim();
      if (!monthsByName[key]) monthsByName[key] = new Set();
      monthsByName[key].add(e.date.substring(0, 7)); // YYYY-MM
    });

    // 2. Extraire de "filteredExpenses" celles qui apparaissent sur au moins 2 mois
    const mappedSubs = new Map<string, Expense>();
    filteredExpenses.forEach(e => {
      if (e.type !== 'expense') return;
      const key = e.name.toLowerCase().trim();
      // On le considère comme abonnement UNIQUEMENT si vu sur plusieurs mois
      if (monthsByName[key] && monthsByName[key].size > 1) {
        const existing = mappedSubs.get(key);
        if (!existing || new Date(e.date) > new Date(existing.date)) {
          mappedSubs.set(key, e);
        }
      }
    });

    return Array.from(mappedSubs.values());
  }, [expenses, filteredExpenses]);

  // --- EXPORT PDF ---
  const handleExportPDF = () => {
    const doc = new jsPDF();
    const dateStr = new Date().toLocaleDateString('fr-FR');

    // Titre
    doc.setFontSize(18);
    doc.text(`Rapport Financier - ${filterMonth || 'Global'}`, 14, 22);

    // Balance
    doc.setFontSize(12);
    doc.text(`Généré le: ${dateStr}`, 14, 30);
    doc.text(`Total Revenus: +${totalIncome.toFixed(2)} EUR`, 14, 40);
    doc.text(`Total Dépenses: -${totalExpenses.toFixed(2)} EUR`, 14, 46);
    doc.text(`Balance: ${balance.toFixed(2)} EUR`, 14, 52);

    // Tableau des données
    const tableData = filteredExpenses.map(e => [
      new Date(e.date).toLocaleDateString('fr-FR'),
      e.name,
      e.category,
      e.type === 'income' ? `+${e.amount.toFixed(2)} €` : `-${e.amount.toFixed(2)} €`,
      e.is_subscription ? 'Oui' : 'Non'
    ]);

    autoTable(doc, {
      startY: 60,
      head: [['Date', 'Intitulé', 'Catégorie', 'Montant', 'Abonnement']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [139, 92, 246] } // Purple-500
    });

    doc.save(`rapport_depenses_${filterMonth || 'global'}.pdf`);
  };

  return (
    <AdminLayout title="Analyse Financière & Dépenses">
      {/* Messages d'erreur génériques */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl flex items-center gap-2">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {isLoading ? (
        // Skeleton ou Spinner
        <div className="flex justify-center p-12">
          <div className="w-8 h-8 border-4 border-[#1f75cb]/30 border-t-[#1f75cb] rounded-full animate-spin" />
        </div>
      ) : expenses.length === 0 ? (
        // État vide
        <div className="card p-12 text-center">
          <div className="w-16 h-16 bg-[#f5f5f7] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Receipt className="w-8 h-8 text-[#5e5e5e]" />
          </div>
          <h3 className="text-lg font-medium text-[#1d1d1f] mb-2">Vous n'avez pas de dépenses</h3>
          <p className="text-sm text-[#5e5e5e] mb-6">
            Ajoutez un premier document (relevé de compte, facture détaillée) pour générer votre tableau de bord.
          </p>
          
          <div className="flex justify-center">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept="application/pdf"
              className="hidden"
              id="pdf-upload-empty"
            />
            <label
              htmlFor="pdf-upload-empty"
              className={`btn btn-primary cursor-pointer shadow-sm ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}
            >
              {isUploading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Analyse...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <UploadCloud className="w-4 h-4" /> Ajouter un relevé
                </span>
              )}
            </label>
          </div>
        </div>
      ) : (
        // Contenu principal avec animation d'entrée
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">

          {/* LIGNE 1 : FILTRES */}
          <div className="card p-4 flex flex-wrap gap-4 items-end bg-white">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-[#5e5e5e] uppercase">Mois</label>
              <input
                type="month"
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)}
                className="px-3 py-2 bg-[#f5f5f7] border border-[#e5e5e5] rounded-lg text-sm text-[#1d1d1f] focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-[#5e5e5e] uppercase">Montant Min (€)</label>
              <input
                type="number"
                placeholder="0"
                value={filterMinAmount}
                onChange={(e) => setFilterMinAmount(e.target.value)}
                className="w-24 px-3 py-2 bg-[#f5f5f7] border border-[#e5e5e5] rounded-lg text-sm text-[#1d1d1f] focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-[#5e5e5e] uppercase">Montant Max (€)</label>
              <input
                type="number"
                placeholder="1000"
                value={filterMaxAmount}
                onChange={(e) => setFilterMaxAmount(e.target.value)}
                className="w-24 px-3 py-2 bg-[#f5f5f7] border border-[#e5e5e5] rounded-lg text-sm text-[#1d1d1f] focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            {filterCategory && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-[#5e5e5e] uppercase">Catégorie</label>
                <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg text-sm">
                  {getCategoryEmoji(filterCategory)} {filterCategory}
                  <button onClick={() => setFilterCategory(null)} className="ml-2 text-purple-500 hover:text-purple-700">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}

            <div className="ml-auto flex gap-2">
              {(filterMonth !== '' || filterCategory || filterMinAmount || filterMaxAmount) && (
                <button
                  onClick={() => {
                    setFilterMonth('');
                    setFilterCategory(null);
                    setFilterMinAmount('');
                    setFilterMaxAmount('');
                  }}
                  className="px-3 py-2 text-sm text-[#5e5e5e] hover:bg-[#f5f5f7] rounded-lg transition-colors"
                >
                  Effacer
                </button>
              )}
            </div>
          </div>

          {/* LIGNE 2 : BALANCE & IA ADVICE */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Balance Card */}
            <div className="card p-6 flex flex-col justify-center bg-gradient-to-br from-gray-900 to-[#1d1d1f] text-white">
              <h3 className="text-white/70 font-medium mb-1">Balance du mois</h3>
              <div className={`text-4xl font-bold ${balance >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {balance >= 0 ? '+' : ''}{balance.toFixed(2)} €
              </div>
              <div className="mt-6 grid grid-cols-2 gap-4">
                <div className="bg-white/10 p-4 rounded-xl backdrop-blur-sm">
                  <div className="flex items-center gap-2 text-green-400 mb-1">
                    <TrendingUp className="w-4 h-4" /> <span className="text-sm">Recettes</span>
                  </div>
                  <div className="text-xl font-semibold">{totalIncome.toFixed(2)} €</div>
                </div>
                <div className="bg-white/10 p-4 rounded-xl backdrop-blur-sm">
                  <div className="flex items-center gap-2 text-red-400 mb-1">
                    <TrendingDown className="w-4 h-4" /> <span className="text-sm">Dépenses</span>
                  </div>
                  <div className="text-xl font-semibold">{totalExpenses.toFixed(2)} €</div>
                </div>
              </div>
            </div>

            {/* IA Advice Card */}
            <div className="card p-6 border border-purple-100 bg-gradient-to-br from-purple-50/50 to-white relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10 pointer-events-none">
                <Sparkles className="w-24 h-24 text-purple-600" />
              </div>

              <div className="flex justify-between items-start mb-4 relative z-10">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-purple-600" />
                  </div>
                  <h3 className="font-bold text-[#1d1d1f]">Conseiller IA</h3>
                </div>

                <button
                  onClick={handleGenerateAdvice}
                  disabled={isGeneratingAdvice || filteredExpenses.length === 0}
                  className="relative group overflow-hidden rounded-xl px-4 py-2 text-sm font-semibold text-white shadow-lg transition-all hover:scale-105 hover:shadow-purple-500/25 active:scale-95 disabled:pointer-events-none disabled:opacity-50 disabled:grayscale"
                >
                  {/* Effet de gradient de fond animé */}
                  <span className="absolute inset-0 bg-gradient-to-r from-purple-600 via-fuchsia-500 to-indigo-600 bg-[length:200%_auto] bg-[0%_center] transition-all duration-500 group-hover:bg-[100%_center]"></span>
                  
                  {/* Bordure brillante */}
                  <span className="absolute inset-0 rounded-xl border border-white/20"></span>
                  
                  {/* Contenu du bouton */}
                  <span className="relative flex items-center justify-center gap-2 drop-shadow-sm">
                    {isGeneratingAdvice ? (
                       <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : <Sparkles className="w-4 h-4 animate-pulse" />}
                    Générer Analyse IA
                  </span>
                </button>
              </div>

              <div className="relative z-10 min-h-[100px] flex items-center">
                <AnimatePresence mode="wait">
                  {aiAdvice ? (
                    <motion.div
                      key="advice"
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-sm text-[#1d1d1f] leading-relaxed"
                    >
                      {renderAiAdvice(aiAdvice)}
                    </motion.div>
                  ) : (
                    <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-sm text-[#5e5e5e] italic">
                      Cliquez sur le bouton "Analyser" pour générer un conseil personnalisé basé sur vos dépenses filtrées.
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* LIGNE 3 : CATEGORIES & RECURRING */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Categories */}
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-[#1d1d1f] flex items-center gap-2">
                  <PieChart className="w-4 h-4 text-purple-600" />
                  Dépenses par Catégorie
                </h3>
              </div>
              {sortedCategories.length > 0 ? (
                <div className="flex flex-col gap-6">
                  {/* RECHARTS PIECHART */}
                  <div className="h-[200px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <RePieChart>
                        <Pie
                          data={sortedCategories.map(([name, value]) => ({ name, value }))}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="value"
                          stroke="none"
                        >
                          {sortedCategories.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value: any) => [`${Number(value).toFixed(2)} €`, 'Montant']}
                          contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                        />
                      </RePieChart>
                    </ResponsiveContainer>
                  </div>

                  {/* LEGEND/BUTTONS */}
                  <div className="space-y-2 max-h-[250px] overflow-y-auto pr-2 custom-scrollbar">
                  {sortedCategories.map(([category, amount], index) => {
                    const isSelected = filterCategory === category;
                    const percentage = totalExpenses > 0 ? Math.round((amount / totalExpenses) * 100) : 0;

                    return (
                      <button
                        key={category}
                        onClick={() => setFilterCategory(isSelected ? null : category)}
                        className={`w-full p-3 rounded-xl flex items-center justify-between transition-colors border ${
                          isSelected
                            ? 'shadow-sm'
                            : 'bg-[#f5f5f7] border-transparent hover:border-[#e5e5e5]'
                        }`}
                        style={isSelected ? { borderColor: PIE_COLORS[index % PIE_COLORS.length], backgroundColor: `${PIE_COLORS[index % PIE_COLORS.length]}15` } : {}}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-lg">{getCategoryEmoji(category)}</span>
                          <div className="flex flex-col items-start">
                            <span className="font-medium text-[#1d1d1f] text-sm text-left">{category}</span>
                            <span className="text-xs text-[#5e5e5e]">{percentage}% des dépenses</span>
                          </div>
                        </div>
                        <span className="font-semibold text-[#1d1d1f]">{amount.toFixed(2)} €</span>
                      </button>
                    )
                  })}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-[#5e5e5e] text-center p-4">Aucune dépense dans cette sélection.</div>
              )}
            </div>

            {/* Subscriptions */}
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-[#1d1d1f] flex items-center gap-2">
                  <Receipt className="w-4 h-4 text-indigo-600" />
                  Abonnements & Prélèvements
                </h3>
              </div>
              {subscriptions.length > 0 ? (
                <div className="space-y-2 max-h-[250px] overflow-y-auto pr-2 custom-scrollbar">
                  {subscriptions.map(sub => (
                    <div key={sub.id} className="p-3 bg-[#f5f5f7] rounded-xl flex items-center justify-between">
                       <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center">
                            {getSubscriptionIcon(sub.name)}
                          </div>
                          <span className="font-medium text-[#1d1d1f] text-sm">{sub.name}</span>
                       </div>
                       <span className="font-semibold text-[#1d1d1f]">{sub.amount.toFixed(2)} €</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-[#5e5e5e] text-center p-4">Aucun abonnement détecté.</div>
              )}
            </div>
          </div>

          {/* LIGNE 4 : ACTIONS (Upload & Export) */}
          <div className="flex flex-wrap gap-4 justify-between items-center bg-white p-4 rounded-2xl border border-[#e5e5e5] shadow-sm">
             <div className="flex items-center gap-4">
               <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept="application/pdf"
                className="hidden"
                id="pdf-upload-bottom"
              />
              <label
                htmlFor="pdf-upload-bottom"
                className={`btn btn-primary cursor-pointer shadow-sm ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}
              >
                {isUploading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Analyse...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <UploadCloud className="w-4 h-4" /> Ajouter un relevé
                  </span>
                )}
              </label>
             </div>

             <button
               onClick={handleExportPDF}
               disabled={filteredExpenses.length === 0}
               className="btn bg-[#f5f5f7] text-[#1d1d1f] border border-[#e5e5e5] hover:bg-[#e5e5e5] disabled:opacity-50 flex items-center gap-2"
             >
               <Download className="w-4 h-4" /> Exporter PDF
             </button>
          </div>

          {/* LIGNE 5 : TABLEAU LISTE */}
          <div className="card overflow-hidden">
            <div className="p-6 border-b border-[#e5e5e5]">
              <h3 className="font-semibold text-[#1d1d1f]">Détails des Opérations ({filteredExpenses.length})</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-[#f5f5f7] border-b border-[#e5e5e5]">
                    <th className="p-4 text-xs font-semibold text-[#5e5e5e] uppercase tracking-wider">Date</th>
                    <th className="p-4 text-xs font-semibold text-[#5e5e5e] uppercase tracking-wider">Intitulé</th>
                    <th className="p-4 text-xs font-semibold text-[#5e5e5e] uppercase tracking-wider">Catégorie</th>
                    <th className="p-4 text-xs font-semibold text-[#5e5e5e] uppercase tracking-wider text-right">Montant</th>
                    <th className="p-4 text-xs font-semibold text-[#5e5e5e] uppercase tracking-wider text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e5e5e5]">
                  {filteredExpenses.map((expense) => (
                    <tr key={expense.id} className="hover:bg-[#f5f5f7]/50 transition-colors">
                      <td className="p-4 text-sm text-[#5e5e5e]">
                        <div className="flex items-center gap-2 whitespace-nowrap">
                          <Calendar className="w-3 h-3 opacity-50" />
                          {new Date(expense.date).toLocaleDateString('fr-FR')}
                        </div>
                      </td>
                      <td className="p-4 font-medium text-[#1d1d1f]">
                        <div className="flex items-center gap-2">
                          {expense.name}
                          {expense.is_subscription && (
                             <span className="px-1.5 py-0.5 bg-indigo-50 text-indigo-600 rounded text-[10px] font-bold uppercase tracking-wider">Abo</span>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <button
                          onClick={() => setFilterCategory(expense.category)}
                          className="badge bg-gray-50 text-gray-700 border-gray-200 hover:bg-purple-50 hover:text-purple-700 hover:border-purple-200 transition-colors flex items-center gap-1.5"
                          title="Filtrer par cette catégorie"
                        >
                          <span>{getCategoryEmoji(expense.category)}</span>
                          {expense.category}
                        </button>
                      </td>
                      <td className={`p-4 text-right font-semibold ${expense.type === 'income' ? 'text-green-600' : 'text-[#1d1d1f]'}`}>
                        {expense.type === 'income' ? '+' : '-'}{expense.amount.toFixed(2)} €
                      </td>
                      <td className="p-4 text-right">
                        <button
                          onClick={() => handleDelete(expense.id)}
                          className="p-2 text-[#5e5e5e] hover:text-[#dc3545] hover:bg-red-50 rounded-lg transition-colors inline-flex"
                          title="Supprimer la ligne"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {filteredExpenses.length === 0 && (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-[#5e5e5e]">Aucune opération ne correspond à vos filtres.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>
      )}
    </AdminLayout>
  );
}
